// Stage 48 §17.2: two-screen "New connection" wizard. The form screen
// collects label / scopes / allowed_origin / rate_limit / expires_at /
// notes; on submit the worker mints a token and we transition to the
// reveal-once screen. Closing the modal returns the user to the list.

import { useState } from 'react';
import {
  ALL_SCOPES,
  createConnection,
  type ConnectionListRow,
  type NewConnectionInput,
  type Scope,
} from '../api/connections';
import { RevealOnceField } from './RevealOnceField';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export function NewConnectionModal({ onClose, onCreated }: Props) {
  const [mode, setMode] = useState<'form' | 'reveal'>('form');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{
    plaintext: string;
    connection: ConnectionListRow;
  } | null>(null);

  // Form state
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<Scope[]>(['image']);
  const [origin, setOrigin] = useState('');
  const [rpm, setRpm] = useState<string>('60');
  const [expiresAt, setExpiresAt] = useState<string>(''); // datetime-local
  const [notes, setNotes] = useState('');

  function toggleScope(s: Scope, on: boolean) {
    setScopes((prev) =>
      on ? [...new Set([...prev, s])] : prev.filter((x) => x !== s),
    );
  }

  async function submit(): Promise<void> {
    setErr(null);

    if (!label.trim()) return setErr('Label is required.');
    if (scopes.length === 0) return setErr('Pick at least one scope.');
    if (!origin.trim()) return setErr('Allowed origin is required.');

    let rpmNum: number | undefined = undefined;
    if (rpm.trim()) {
      const n = parseInt(rpm, 10);
      if (!Number.isFinite(n) || n < 1 || n > 6000) {
        return setErr('Rate limit must be a number between 1 and 6000.');
      }
      rpmNum = n;
    }

    let expiresAtUnix: number | null = null;
    if (expiresAt) {
      const d = new Date(expiresAt);
      if (Number.isNaN(d.getTime())) {
        return setErr('Invalid expiry date.');
      }
      expiresAtUnix = Math.floor(d.getTime() / 1000);
      if (expiresAtUnix <= Math.floor(Date.now() / 1000)) {
        return setErr('Expiry must be in the future.');
      }
    }

    const body: NewConnectionInput = {
      label: label.trim(),
      scopes,
      allowed_origin: origin.trim(),
      rate_limit_per_minute: rpmNum,
      expires_at: expiresAtUnix,
      notes: notes.trim() || null,
    };

    setBusy(true);
    try {
      const res = await createConnection(body);
      setRevealed({
        plaintext: res.plaintext_token,
        connection: res.connection,
      });
      setMode('reveal');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function close(): void {
    if (mode === 'reveal') {
      // The user has had their one chance. Refresh the list.
      onCreated();
      onClose();
    } else {
      onClose();
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="modal modal-settings">
        <header className="modal-header">
          <h2>
            {mode === 'form' ? 'New connection' : 'Save your token'}
          </h2>
          <button
            className="modal-close"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {mode === 'form' ? (
          <section className="modal-body">
            <div className="panel">
              <label className="form-label">
                Label
                <input
                  type="text"
                  maxLength={80}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="My portfolio site"
                  disabled={busy}
                />
              </label>

              <fieldset className="form-fieldset">
                <legend>Allowed scopes</legend>
                <div className="scope-grid">
                  {ALL_SCOPES.map((s) => (
                    <label key={s} className="voice-toggle-row">
                      <input
                        type="checkbox"
                        checked={scopes.includes(s)}
                        onChange={(e) => toggleScope(s, e.target.checked)}
                        disabled={busy}
                      />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
                <p className="muted small">
                  At least one is required. Each scope maps to one
                  /api/public/v1/gallery/{'<scope>'} sub-tree.
                </p>
              </fieldset>

              <label className="form-label">
                Allowed origin (single URL)
                <input
                  type="text"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="https://my-portfolio.example.com"
                  disabled={busy}
                  autoComplete="off"
                  spellCheck={false}
                />
                <span className="muted small">
                  https:// only (http://localhost permitted for dev). No
                  paths or trailing slashes.
                </span>
              </label>

              <label className="form-label">
                Rate limit (requests / minute)
                <input
                  type="number"
                  min={1}
                  max={6000}
                  step={1}
                  value={rpm}
                  onChange={(e) => setRpm(e.target.value)}
                  disabled={busy}
                />
              </label>

              <label className="form-label">
                Expires (optional)
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  disabled={busy}
                />
                <span className="muted small">
                  Leave blank for no expiry.
                </span>
              </label>

              <label className="form-label">
                Notes (optional)
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What this token is for…"
                  disabled={busy}
                />
              </label>

              {err ? (
                <div className="status-missing">Error: {err}</div>
              ) : null}
            </div>
          </section>
        ) : (
          <section className="modal-body">
            <div className="panel">
              {revealed ? (
                <>
                  <RevealOnceField
                    value={revealed.plaintext}
                    label="Plaintext token"
                  />
                  <p className="muted small" style={{ marginTop: 12 }}>
                    Connection: <strong>{revealed.connection.label}</strong>
                    {' · '}
                    prefix <code>{revealed.connection.token_prefix}</code>
                  </p>
                  <p className="muted small">
                    Scopes: {revealed.connection.scopes.join(', ')}
                    {' · '}
                    Origin: <code>{revealed.connection.allowed_origin}</code>
                  </p>
                </>
              ) : null}
            </div>
          </section>
        )}

        <footer className="modal-footer">
          {mode === 'form' ? (
            <>
              <button onClick={close} disabled={busy}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => void submit()}
                disabled={busy}
              >
                {busy ? 'Creating…' : 'Create connection'}
              </button>
            </>
          ) : (
            <button className="btn-primary" onClick={close}>
              Close
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
